//src/lib/context/services/components/ServicesProvider.tsx

import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgendaManager as AgendaManagerInstance } from "../../../managers/agenda";
import { TARGET_API_V2 } from "../../../managers/api";
import {
    isB2CUserNotAllowedError,
    isNonRetryableApiError,
} from "../../../managers/api/errors";
import { FrontendError } from "../../../managers/api/types/apiResponse";
import type { ClientInfo } from "../../../managers/api/types/clientInfo";
import { ConsentManager as ConsentManagerInstance } from "../../../managers/consentManager";
import type { ConversationContract } from "../../../managers/conversation/types/conversation";
import { CustomViewManager as CustomViewManagerInstance } from "../../../managers/customView";
import { LicenseManager as LicenseManagerInstance } from "../../../managers/license";
import { MailManager as MailManagerInstance } from "../../../managers/mail";
import { UserManager as UserManagerInstance } from "../../../managers/user";
import { createSdk } from "../../../sdk/createSdk";
import { logger, LOGS } from "../../../utils";
import type { UserTimezoneReader } from "../../../utils/date/userTimezone";
import { AuthUser, useAuthContext } from "../../auth";
import { useEventBus } from "../../events";
import { useNetworkStatus } from "../../network";
import { ServicesProviderContext } from "../hooks/use-services-context";
import {
    SdkServices,
    ServiceContextValue,
    StartupPhase,
} from "../types/services";
import {
    INITIAL_ANALYSIS_DASHBOARD_TIMEOUT_MS,
    waitForInitialAnalysis,
} from "../utils/wait-for-initial-analysis";

async function withRetry<T>(
    fn: () => Promise<T>,
    methodName: string,
    delayMs = 2000,
): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        logger.error(
            LOGS.ServiceProvider,
            `${methodName} failed !!! : ${error}`,
        );

        if (isNonRetryableApiError(error)) {
            logger.warn(
                LOGS.ServiceProvider,
                `${methodName} : refus fonctionnel du Backend, pas de retry`,
            );
            throw error;
        }

        logger.warn(
            LOGS.ServiceProvider,
            `${methodName} will retry once after ${delayMs}ms...`,
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return await fn();
    }
}

type ServicesProviderProps = PropsWithChildren<{
    apiTarget_v2?: TARGET_API_V2;
    /** Identité du client, pour `X-Mv-Client` (F-1.24). Absente : en-têtes non envoyés. */
    clientInfo?: ClientInfo;
    /** Lecture du fuseau de l'appareil, pour `userTimezone` (F-1.19). Absente : `Intl`. */
    readUserTimezone?: UserTimezoneReader;
    /** Contrat de l'assistant avec l'Api-Ai. Absent : v1. */
    conversationContract?: ConversationContract;
}>;

export function ServicesProvider({
    children,
    apiTarget_v2 = TARGET_API_V2.Api_Dev,
    clientInfo,
    readUserTimezone,
    conversationContract,
}: ServicesProviderProps) {
    const uniqueId = useRef(Math.floor(Math.random() * 1000));

    useEffect(() => {
        console.log(
            `[${uniqueId.current}] ServicesProvider MOUNTED - apiBackendTarget : ${apiTarget_v2}`,
        );

        return () => {
            console.log(`[${uniqueId.current}] ServicesProvider UNMOUNTED`);
        };
    }, []); // tableau vide = uniquement au montage/démontage

    // #region Properties

    const servicesRef = useRef<SdkServices | null>(null);
    const initFailedRef = useRef(false);

    // État machine à états du startup — remplace isInitialized, isConsentManagerReady et startupAbortedRef
    // Toujours passer par setPhase() plutôt qu'appeler setStartupPhase directement.
    const [startupPhase, setStartupPhase] = useState<StartupPhase>("idle");
    const startupPhaseRef = useRef<StartupPhase>("idle");

    // AB#1725 — Chronométrage des étapes de démarrage. Chaque transition logue la durée de la
    // phase qui se termine + le total depuis phase0 (logs `[STARTUP-TIMING]`, greppables — servent
    // à mettre à jour Doc/AB#1725-Demarrage à partir d'un run en conditions réelles).
    // t0 = entrée en phase0_running ; réinitialisé à chaque nouveau démarrage (re-login post-cleanup).
    const startupTimingRef = useRef<{
        startedAt: number;
        lastTransitionAt: number;
    } | null>(null);

    const authContext = useAuthContext();
    const { isAuthenticated } = authContext;

    const eventBusRef = useRef(useEventBus());
    const eventBus = eventBusRef.current;

    const networkStatus = useNetworkStatus();
    const networkStatusRef = useRef(networkStatus);
    networkStatusRef.current = networkStatus;

    // Indique si l'utilisateur se connecte pour la première fois (positionné en fin de Phase 1)
    // Utilisé par _runPhase3 pour choisir le délai UI approprié avant le Dashboard
    const isNewUserRef = useRef<boolean>(false);

    // Race « fin d'analyse initiale OU cap 3 min » (nouvel utilisateur uniquement).
    const analysisRacePromiseRef = useRef<Promise<void> | undefined>(undefined);

    // Refs vers les managers nécessitant une initialisation post-consentement
    const mailManagerRef = useRef<MailManagerInstance | null>(null);
    const customViewManagerRef = useRef<CustomViewManagerInstance | null>(null);
    const agendaManagerRef = useRef<AgendaManagerInstance | null>(null);
    const userManagerRef = useRef<UserManagerInstance | null>(null);
    const consentManagerRef = useRef<ConsentManagerInstance | null>(null);
    const licenseManagerRef = useRef<LicenseManagerInstance | null>(null);

    const disposeSessionRef = useRef<(() => void) | null>(null);

    // #endregion Properties

    // #region Helpers

    /**
     * Transition de phase : met à jour le ref (lecture synchrone dans les callbacks async)
     * et le state React (déclenchement du re-render pour les consommateurs du contexte).
     */

    const setPhase = useCallback((phase: StartupPhase) => {
        // AB#1725 — [STARTUP-TIMING] durée de l'étape qui se termine + cumul depuis phase0.
        const now = performance.now();
        if (phase === "idle") {
            // Retour au repos (cleanup/logout) : pas une étape de démarrage — on désarme le chrono
            // sans logger (sinon on mesurerait le temps passé sur le Dashboard).
            startupTimingRef.current = null;
        } else if (phase === "phase0_running") {
            startupTimingRef.current = {
                startedAt: now,
                lastTransitionAt: now,
            };
            logger.info(
                [LOGS.ServiceProvider, LOGS.AB1725],
                `[STARTUP-TIMING] → phase0_running (t0)`,
            );
        } else if (startupTimingRef.current) {
            const timing = startupTimingRef.current;
            const stepMs = Math.round(now - timing.lastTransitionAt);
            const totalMs = Math.round(now - timing.startedAt);
            logger.info(
                [LOGS.ServiceProvider, LOGS.AB1725],
                `[STARTUP-TIMING] ${startupPhaseRef.current} → ${phase} : +${stepMs} ms (total ${totalMs} ms)`,
            );
            timing.lastTransitionAt = now;
        }

        logger.info(
            LOGS.ServiceProvider,
            `ServicesProvider - startup phase → ${phase}`,
        );
        startupPhaseRef.current = phase;
        setStartupPhase(phase);
    }, []);

    /**
     * Indique si le startup a été avorté (réseau ou échec de phase).
     *
     * Note : le type annotation explicite sur `phase` est intentionnel.
     * TypeScript réalise un narrowing intra-procédural incorrect en excluant 'error'
     * après des blocs catch+return, mais le ref peut être muté à 'error' par des
     * event handlers externes (ex: network-offline-threshold-reached).
     * La variable intermédiaire explicitement typée StartupPhase court-circuite ce narrowing.
     */
    const isStartupAborted = useCallback((): boolean => {
        const phase: StartupPhase = startupPhaseRef.current;
        return phase === "error";
    }, []);

    // #endregion Helpers

    // #region Startup Phases

    /**
     * Phase 1 — consentManager + licenseManager
     * Lancée automatiquement après Phase 0.
     * Le bootstrap utilisateur est déjà terminé — un retry ici ne le rejoue pas.
     * La complétion déclenche la transition vers 'awaiting_consent'.
     */
    const _runPhase1 = useCallback((): void => {
        logger.info(
            LOGS.ServiceProvider,
            "  => Launching Phase 1: Consent + License initialization...",
        );
        setPhase("phase1_running");

        withRetry(
            () =>
                Promise.all([
                    consentManagerRef.current!._initialize(),
                    licenseManagerRef.current!._initialize(),
                ]),
            "Phase 1 [consentManager._initialize, licenseManager._initialize]",
        )
            .then(() => {
                if (isStartupAborted()) return;
                logger.info(LOGS.ServiceProvider, `====== Phase 1 done ======`);
                setPhase("phase1_awaiting_consent");
            })
            .catch((error) => {
                logger.error(
                    LOGS.ServiceProvider,
                    `Phase 1 failed after retry: ${error}`,
                );
                logger.error(
                    [],
                    " !!! APP CANNOT START DUE TO ERRORS IN SERVICES INITIALISATION !!!",
                );
                setPhase("error");
                eventBus.emit(
                    "startup-failed",
                    FrontendError.USER_OR_CONSENT_MANAGER_INITIALISATION_FAILED,
                );
            });
    }, [eventBus, setPhase, isStartupAborted]);

    /**
     * Phase 0 — Bootstrap utilisateur (isolé)
     * Appelle userManager._initialize() (qui inclut l'appel bootstrap API + seed licenses).
     * Isolé dans sa propre phase avec retry dédié pour que :
     * - Le bootstrap ne soit pas rejoué si seule la Phase 1 échoue et retry
     * - isNewUser soit acquis définitivement après un bootstrap réussi
     *
     * Fire & forget : la complétion enchaîne automatiquement vers _runPhase1.
     */
    const _runPhase0 = useCallback((): void => {
        logger.info(
            LOGS.ServiceProvider,
            "  => Launching Phase 0: User bootstrap...",
        );
        setPhase("phase0_running");

        withRetry(
            () => userManagerRef.current!._initialize(),
            "Phase 0 [userManager._initialize]",
        )
            .then(() => {
                if (isStartupAborted()) return;
                isNewUserRef.current =
                    userManagerRef.current?._isUserJustCreated() ?? false;
                logger.info(
                    LOGS.ServiceProvider,
                    `====== Phase 0 done — isNewUser: ${isNewUserRef.current} ======`,
                );
                _runPhase1();
            })
            .catch((error) => {
                if (isB2CUserNotAllowedError(error)) {
                    logger.warn(
                        LOGS.ServiceProvider,
                        `Phase 0 refusee : compte B2C non autorise par le Backend`,
                    );
                    setPhase("error");
                    eventBus.emit(
                        "startup-failed",
                        FrontendError.USER_B2C_NOT_ALLOWED,
                    );

                    return;
                }

                logger.error(
                    LOGS.ServiceProvider,
                    `Phase 0 failed after retry: ${error}`,
                );
                logger.error(
                    [],
                    " !!! APP CANNOT START DUE TO ERRORS IN SERVICES INITIALISATION !!!",
                );
                setPhase("error");
                eventBus.emit(
                    "startup-failed",
                    FrontendError.USER_OR_CONSENT_MANAGER_INITIALISATION_FAILED,
                );
            });
    }, [eventBus, setPhase, isStartupAborted, _runPhase1]);

    /**
     * Phase 2 — customViewManager + userManager post-consent
     * Lancée après réception de 'user-consents-updated'.
     */
    const _runPhase2 = useCallback(async (): Promise<void> => {
        logger.info(
            LOGS.ServiceProvider,
            "  => Launching Phase 2: CustomView + UserContext initialization...",
        );
        setPhase("phase2_running");

        // AB#1721 — Signaler l'écran de chargement dès le post-consentement (isNewUser connu depuis la Phase 0),
        // au lieu d'attendre la fin de Phase 3 : le nouvel utilisateur voit son écran spécifique plus tôt.
        if (isNewUserRef.current) {
            logger.info(
                [LOGS.ServiceProvider],
                "  => Emitting 'startup-new-user-analysis-loading' (first connection loading screen)",
            );
            eventBus.emit("startup-new-user-analysis-loading");

            // Armer le cap 3 min ICI, à l'affichage de l'écran de bienvenue
            analysisRacePromiseRef.current = waitForInitialAnalysis(eventBus);
        } else {
            logger.info(
                [LOGS.ServiceProvider],
                "  => Emitting 'startup-returning-user-loading' (returning user loading screen)",
            );
            eventBus.emit("startup-returning-user-loading");
        }

        try {
            await withRetry(
                () =>
                    Promise.all([
                        customViewManagerRef.current!._initialize(),
                        userManagerRef.current!._initializePostConsent(),
                    ]),
                "Phase 2 [customViewManager._initialize, userManager._initializePostConsent]",
            );
            logger.info(LOGS.ServiceProvider, "====== Phase 2 done ======");
        } catch (error) {
            logger.error(
                LOGS.ServiceProvider,
                `Phase 2 failed after retry: ${error}`,
            );
            logger.error(
                [],
                " !!! APP CANNOT START DUE TO ERRORS IN SERVICES INITIALISATION !!!",
            );
            setPhase("error");
            eventBus.emit(
                "startup-failed",
                FrontendError.SCORING_OR_CUSTOMVIEW_MANAGER_INITIALISATION_FAILED,
            );
        }
    }, [eventBus, setPhase]);

    /**
     * Phase 3 — mailManager
     * Après l'initialisation, applique un délai UI avant d'afficher le Dashboard :
     * - Reconnexion       : 4s fixes (affichage du logo de chargement)
     * - 1ère connexion    : race entre 'initial-analysis-completed' (émis par MailManager)
     *                       et un timeout de sécurité de 3 min (AB#1725) — le premier déclenche
     *                       la transition.
     * La complétion déclenche la transition vers 'phase4_running' et affiche le Dashboard.
     */
    const _runPhase3 = useCallback(async (): Promise<void> => {
        logger.info(
            LOGS.ServiceProvider,
            "  => Launching Phase 3: Mail initialization (to go to dashboard screen)...",
        );
        setPhase("phase3_running");

        try {
            // ─────────────────────────────────────────────────────────────────────
            // la race est armée en début de Phase 2 (affichage de l'écran de bienvenue)
            // ─────────────────────────────────────────────────────────────────────
            let analysisRacePromise: Promise<void> | undefined;
            if (isNewUserRef.current) {
                analysisRacePromise =
                    analysisRacePromiseRef.current ??
                    waitForInitialAnalysis(eventBus);
                analysisRacePromiseRef.current = undefined;
            }
            // ─────────────────────────────────────────────────────────────────────

            await withRetry(
                () => mailManagerRef.current!._initialize(),
                "Phase 3 [mailManager._initialize]",
            );
            logger.info(LOGS.ServiceProvider, "====== Phase 3 done ======");

            // Délai UI avant affichage du Dashboard — piloté par ServicesProvider (hors MailManager).
            // L'écran de chargement a déjà été signalé au FrontEnd en début de Phase 2 (AB#1721).
            if (isNewUserRef.current) {
                logger.info(
                    LOGS.ServiceProvider,
                    `  => First connection: waiting for initial mail analysis (cap ${INITIAL_ANALYSIS_DASHBOARD_TIMEOUT_MS / 1000}s armé à l'affichage de l'écran de bienvenue, début Phase 2)...`,
                );
                setPhase("phase3_awaiting_initial_analysis");
                await analysisRacePromise!; // Résolu par l'event OU le timeout 3 min (le premier gagne)
            } else {
                logger.info(
                    LOGS.ServiceProvider,
                    "  => Returning user: 4s logo display...",
                );
                setPhase("phase3_awaiting_ready");
                await new Promise<void>((resolve) => setTimeout(resolve, 3900));
            }

            logger.info(
                LOGS.ServiceProvider,
                "  => ServicesProvider Initialisation done with success.",
            );
            setPhase("phase4_running");
            eventBus.emit("services-ready");
        } catch (error) {
            // Note (assumé) : si _initialize() throw, analysisRacePromise est abandonnée ici — son
            // listener 'initial-analysis-completed' et son timer restent armés jusqu'à 3 min max
            // (INITIAL_ANALYSIS_DASHBOARD_TIMEOUT_MS). Inoffensif : résoudre une promesse non-awaitée
            // est un no-op, et le timeout finit par tout nettoyer (off + clearTimeout).
            logger.error(
                LOGS.ServiceProvider,
                `Phase 3 failed after retry: ${error}`,
            );
            logger.error(
                LOGS.ServiceProvider,
                " !!! APP CANNOT START DUE TO ERRORS IN SERVICES INITIALISATION !!!",
            );
            setPhase("error");
            eventBus.emit(
                "startup-failed",
                FrontendError.MAILMANAGER_INITIALISATION_FAILED,
            );
        }
    }, [eventBus, setPhase]);

    /**
     * Phase 4 — agendaManager + userManager + mailManager (startUp)
     * Tâches de fond post-Dashboard. Erreur non bloquante pour l'UI.
     */
    const _runPhase4 = useCallback(async (): Promise<void> => {
        logger.info(
            LOGS.ServiceProvider,
            "  => Launching Phase 4: Agenda + User + Mail startUp...",
        );

        try {
            await withRetry(
                () =>
                    Promise.all([
                        agendaManagerRef.current!._startUp(),
                        userManagerRef.current!._startUp(),
                        mailManagerRef.current!._startUp(),
                    ]),
                "Phase 4 [agendaManager._startUp, userManager._startUp, mailManager._startUp]",
            );
            logger.info(LOGS.ServiceProvider, "====== Phase 4 done ======");
            // Après la synchro : les mails sans ligne `mail` sont vérifiés auprès de Graph, sans bloquer le démarrage
            void userManagerRef.current?._purgeTodosOfPermanentlyDeletedMails();
            setPhase("startup_complete");
        } catch (error) {
            // Phase 4 = tâches de fond post-Dashboard (agenda, user refresh, mail polling).
            // L'utilisateur est déjà sur le Dashboard → ne pas rétrograder isInitialized.
            // Les données manquantes seront rechargées à la prochaine interaction.
            logger.warn(
                LOGS.ServiceProvider,
                `Phase 4 failed after retry (non-blocking, Dashboard unaffected): ${error}`,
            );
            setPhase("startup_complete"); // Machine à états propre — pas de régression UI
        }
    }, [setPhase]);

    // #endregion Startup Phases

    // #region Initialization

    /**
     * Instancie et câble tous les managers, met en place la surveillance réseau,
     * puis orchestre le démarrage en phases via _runPhase1..4.
     */
    const initializeServices = useCallback(
        (user: AuthUser, userMail: string): SdkServices | null => {
            logger.info(
                LOGS.ServiceProvider,
                `ServicesProvider - initializeServices deps changed :
        eventBus: ${!!eventBus}
        authContext.isAuthenticated: ${authContext.isAuthenticated}`,
            );

            try {
                // --- Validation réseau -----------------------------------------------

                // Check immédiat : le réseau est-il disponible ?
                const currentNetworkStatus = networkStatusRef.current;
                if (currentNetworkStatus.isOnline === false) {
                    logger.error(
                        LOGS.ServiceProvider,
                        "Network is unavailable at startup - aborting initialization",
                    );
                    setPhase("error");
                    eventBus.emit(
                        "startup-failed",
                        FrontendError.NETWORK_UNAVAILABLE_AT_STARTUP,
                    );
                    return null;
                }

                // Surveillance réseau pendant l'initialisation (phases 1-4) : perte > 20s → abort
                const handleNetworkLostDuringStartup = () => {
                    logger.error(
                        LOGS.ServiceProvider,
                        "Network lost during startup (threshold reached) - aborting initialization",
                    );
                    setPhase("error");
                    eventBus.emit(
                        "startup-failed",
                        FrontendError.NETWORK_LOST_DURING_STARTUP,
                    );
                };
                eventBus.on(
                    "network-offline-threshold-reached",
                    handleNetworkLostDuringStartup,
                );

                // Auto-cleanup de la surveillance réseau quand l'init est terminée (succès ou échec)
                const cleanupNetworkMonitoring = () => {
                    eventBus.off(
                        "network-offline-threshold-reached",
                        handleNetworkLostDuringStartup,
                    );
                    eventBus.off("services-ready", cleanupNetworkMonitoring);
                    eventBus.off("startup-failed", cleanupNetworkMonitoring);
                };
                eventBus.on("services-ready", cleanupNetworkMonitoring);
                eventBus.on("startup-failed", cleanupNetworkMonitoring);

                const services = createSdk({
                    eventBus,
                    authContext,
                    apiTarget: apiTarget_v2,
                    clientInfo,
                    readUserTimezone,
                    conversationContract,
                    user,
                    userMail,
                });
                disposeSessionRef.current = services.dispose;

                // --- Alimentation des refs (utilisées par _runPhase1..4) -------------

                mailManagerRef.current = services.mailManager;
                customViewManagerRef.current = services.customViewManager;
                agendaManagerRef.current = services.agendaManager;
                userManagerRef.current = services.userManager;
                consentManagerRef.current = services.consentManager;
                licenseManagerRef.current = services.licenseManager;

                // --- Orchestration des phases post-consentement ----------------------

                const handleConsentsUpdated = async () => {
                    logger.info(
                        LOGS.ServiceProvider,
                        " => processing 'user-consents-updated' event",
                    );
                    eventBus.off(
                        "user-consents-updated",
                        handleConsentsUpdated,
                    );

                    if (isStartupAborted()) return;
                    await _runPhase2();

                    if (isStartupAborted()) return;
                    await _runPhase3();

                    if (isStartupAborted()) return;
                    await _runPhase4();
                };

                logger.info(
                    LOGS.ServiceProvider,
                    " => registration to 'user-consents-updated' done",
                );
                eventBus.on("user-consents-updated", handleConsentsUpdated);

                // --- Lancement de la Phase 0 (fire & forget) -------------------------

                _runPhase0();

                return services;
            } catch (error) {
                logger.error(
                    LOGS.ServiceProvider,
                    `  !!! The instanciation of Managers failed : ${error}`,
                );
                logger.info(
                    LOGS.AnalysisLifecycle,
                    " Initialize : false - Initilization failed",
                );
                setPhase("error");
                eventBus.emit(
                    "startup-failed",
                    FrontendError.MANAGERS_INITIALISATION_FAILED,
                );

                return null;
            }
        },
        [
            eventBus,
            authContext,
            setPhase,
            isStartupAborted,
            _runPhase0,
            _runPhase2,
            _runPhase3,
            _runPhase4,
        ],
    );

    // #endregion Initialization

    // #region Effects

    // Lance l'initialisation dès que l'authentification est prête
    useEffect(() => {
        if (!isAuthenticated) {
            logger.info(
                LOGS.ServiceProvider,
                "  !isAuthenticated || !isInitialized yet. Returning.",
            );
            return;
        }

        const user = authContext.getUser();
        const userId = user.id;
        const userMail = user.email;

        if (!servicesRef.current && !initFailedRef.current) {
            logger.info(
                LOGS.ServiceProvider,
                `Initializing managers with userId ${userId} and userMail ${userMail}`,
            );
            servicesRef.current = initializeServices(user, userMail);
            initFailedRef.current = !servicesRef.current;
        }
    }, [isAuthenticated, initializeServices]);

    // Émet 'startup-consent-manager-ready' après la complétion de la Phase 1
    useEffect(() => {
        if (startupPhase === "phase1_awaiting_consent") {
            logger.info(
                LOGS.ServiceProvider,
                "Emitting 'startup-consent-manager-ready' after context update",
            );
            eventBus.emit("startup-consent-manager-ready");
        }
    }, [startupPhase, eventBus]);

    // Gestion du cleanup complet (déclenché par l'événement 'services-cleanup')
    useEffect(() => {
        const handleCleanup = async () => {
            logger.info(
                LOGS.ServiceProvider,
                `[${uniqueId.current}] ServicesProvider - Starting cleanup process`,
            );

            initFailedRef.current = false;

            if (!servicesRef.current) {
                logger.info(LOGS.ServiceProvider, "  No services to cleanup");
                setPhase("idle");
                eventBus.emit("services-cleanup-completed");
                return;
            }

            // Coupé avant les cleanups : aucun manager déjà nettoyé ne reçoit plus d'événement.
            logger.info(LOGS.ServiceProvider, "  Disposing SDK session");
            disposeSessionRef.current?.();
            disposeSessionRef.current = null;

            // Étape 1 : cleanup de tous les managers
            try {
                logger.info(
                    LOGS.ServiceProvider,
                    "  Step 1: Cleaning up managers",
                );

                const managers = [
                    {
                        name: "conversationManager",
                        instance: servicesRef.current.conversationManager,
                    },
                    {
                        name: "mailManager",
                        instance: servicesRef.current.mailManager,
                    },
                    {
                        name: "customViewManager",
                        instance: servicesRef.current.customViewManager,
                    },
                    {
                        name: "userManager",
                        instance: servicesRef.current.userManager,
                    },
                    {
                        name: "consentManager",
                        instance: servicesRef.current.consentManager,
                    },
                    {
                        name: "licenseManager",
                        instance: servicesRef.current.licenseManager,
                    },
                    {
                        name: "iaManager",
                        instance: servicesRef.current.iaManager,
                    },
                    {
                        name: "dataManager",
                        instance: servicesRef.current.dataManager,
                    },
                    {
                        name: "musicManager",
                        instance: servicesRef.current.musicManager,
                    },
                    {
                        name: "agendaManager",
                        instance: servicesRef.current.agendaManager,
                    },
                    {
                        name: "bannerManager",
                        instance: servicesRef.current.bannerManager,
                    },
                    {
                        name: "contactManager",
                        instance: servicesRef.current.contactManager,
                    },
                    {
                        name: "sttManager",
                        instance: servicesRef.current.sttManager,
                    },
                ];

                const cleanupResults = await Promise.allSettled(
                    managers.map(async ({ name, instance }) => {
                        if (
                            instance &&
                            typeof instance._cleanup === "function"
                        ) {
                            logger.info(
                                LOGS.ServiceProvider,
                                `    Cleaning up ${name}`,
                            );
                            await instance._cleanup();
                            logger.info(
                                LOGS.ServiceProvider,
                                `    ${name} cleanup completed`,
                            );
                        }
                    }),
                );

                cleanupResults.forEach((result, index) => {
                    if (result.status === "rejected") {
                        logger.error(
                            LOGS.ServiceProvider,
                            `    ${managers[index].name} cleanup failed: ${result.reason}`,
                        );
                    }
                });
            } catch (error) {
                logger.error(
                    LOGS.ServiceProvider,
                    `ServicesProvider - Error during managers cleanup: ${error}`,
                );
            }

            // Étape 2 : destruction des instances
            try {
                logger.info(
                    LOGS.ServiceProvider,
                    "  Step 2: Destroying service instances",
                );
                servicesRef.current = null;
            } catch (error) {
                logger.error(
                    LOGS.ServiceProvider,
                    `ServicesProvider - Error destroying instances: ${error}`,
                );
            }

            // Étape 3 : reset de l'état du provider
            try {
                logger.info(
                    LOGS.ServiceProvider,
                    "  Step 5: Resetting provider state",
                );
                setPhase("idle");
            } catch (error) {
                logger.error(
                    LOGS.ServiceProvider,
                    `ServicesProvider - Error resetting state: ${error}`,
                );
            }

            logger.info(
                LOGS.ServiceProvider,
                "ServicesProvider - Cleanup process completed",
            );
            logger.info(
                LOGS.AnalysisLifecycle,
                " Initialize : false - Cleanup completed",
            );

            eventBus.emit("services-cleanup-completed");
        };

        eventBus.on("services-cleanup", handleCleanup);
        return () => eventBus.off("services-cleanup", handleCleanup);
    }, [eventBus, authContext, setPhase]);

    // #endregion Effects

    // Dériver isInitialized et isConsentManagerReady depuis la phase courante.
    // Garantit la rétrocompatibilité avec tous les consommateurs du contexte.
    //
    // isConsentManagerReady : true dès qu'on sort de l'écran de consentement,
    //   c'est-à-dire pour toutes les phases à partir de 'phase1_awaiting_consent' inclus,
    //   y compris les nouvelles phases 'phase3_awaiting_ready' et 'phase3_awaiting_initial_analysis'.
    //   La logique par exclusion couvre automatiquement tout ajout futur de phases intermédiaires.
    //
    // isInitialized : true uniquement quand le Dashboard est affiché (phase4_running ou startup_complete).
    //   Les phases 'phase3_awaiting_ready' et 'phase3_awaiting_initial_analysis' précèdent l'affichage
    //   du Dashboard — elles ne sont donc pas incluses.
    const contextValue = useMemo<ServiceContextValue>(
        () => ({
            sdk: servicesRef.current,
            // La chaîne de démarrage peut atteindre phase4 après un cleanup (logout pendant la Phase 3).
            isInitialized:
                servicesRef.current !== null &&
                (startupPhase === "phase4_running" ||
                    startupPhase === "startup_complete"),
            isConsentManagerReady:
                startupPhase !== "idle" &&
                startupPhase !== "phase0_running" &&
                startupPhase !== "phase1_running" &&
                startupPhase !== "error",
            // AB#1721 — startupType interrogeable (état dérivé, pas event transitoire) : le gate Front
            // le lit à son montage, même monté après l'émission des events startup-*. isNewUser est fixé
            // en fin de Phase 0 ; setPhase('phase1_running') qui suit re-déclenche ce memo → valeur à jour.
            // null tant que non déterminé (idle / phase0) ou après cleanup (retour à idle).
            startupType:
                startupPhase === "idle" || startupPhase === "phase0_running"
                    ? null
                    : isNewUserRef.current
                      ? "new-user"
                      : "returning-user",
        }),
        [startupPhase],
    );

    return (
        <ServicesProviderContext.Provider value={contextValue}>
            {children}
        </ServicesProviderContext.Provider>
    );
}
